export default function TexarkanaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Texarkana Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Texarkana travelers actually move helps us anticipate the passport urgencies that arise here. From railroad industry assignments to mission team departures, the Ark-La-Tex region has a unique travel profile shaped by its tri-state geography and rail-and-logistics economy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">TXK — Texarkana Regional Airport</h3>
                <p className="text-gray-400 text-xs">Texarkana, AR — regional gateway with Dallas connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Texarkana Regional Airport (TXK) offers connections to Dallas-Fort Worth for international departures. Most international Texarkana travelers connect through DFW for transatlantic and transpacific flights. Dallas is approximately 170 miles southwest — making a same-day drive feasible but not ideal when time is short. Many travelers opt to fly through DFW or Little Rock depending on routing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Ensure your passport is valid for at least six months beyond your return date — this is a requirement for most international destinations. Check it the moment you book, not the week before departure.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-train-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Railroad &amp; Logistics Industry Travel</h3>
                <p className="text-gray-400 text-xs">Union Pacific, BNSF, and regional logistics employers</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Texarkana is a major rail junction where Union Pacific and BNSF both maintain significant operations. Railroad professionals attend international training programs, safety certification courses, and supplier meetings in Canada, Mexico, and Europe — often on 2–4 week notice. Logistics managers at regional distribution centers face similar international travel demands with compressed timelines that standard processing cannot accommodate.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Railroad and logistics professionals should treat passport maintenance like equipment certification — renew proactively, not reactively. International assignments rarely wait for passport processing timelines.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">College Study Abroad &amp; Academic Travel</h3>
                <p className="text-gray-400 text-xs">Texarkana College &amp; Texas A&M University-Texarkana</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Texarkana College and TAMUT combined enroll over 8,000 students, many of whom participate in study-abroad programs, international service-learning trips, and faculty research expeditions. Many are first-time passport applicants who discover their need after program acceptance — leaving weeks, not months, to complete the process. The in-person acceptance clerk requirement for new applicants adds a step that must be navigated correctly.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants need an in-person acceptance clerk visit. We guide Texarkana College and TAMUT students through the exact local locations and forms so they do not miss their program departure.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Church Mission &amp; Volunteer Travel</h3>
                <p className="text-gray-400 text-xs">Miller County &amp; Bowie County congregations</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Ark-La-Tex region has one of the most active church mission communities in the South. Hundreds of churches in Miller County, Bowie County, and surrounding areas send teams to Mexico, Guatemala, Honduras, East Africa, and Southeast Asia — often with participant lists finalized just weeks before departure. Mission team leaders routinely discover that first-time travelers have never had a passport, triggering urgent processing needs.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Mission organizers should collect and verify passport information during team sign-up — not the week before departure. Build a two-week processing buffer into your timeline for new applicants.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Between Two States, Between Two Passport Agencies — We Bridge the Gap</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Texarkana sits 140 miles from Little Rock and 170 miles from Dallas-Fort Worth — both home to regional passport agencies. Fast Passport Center eliminates both drives entirely. Start online, ship overnight, and we handle the submission through our reserved courier access.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}